import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import { FaPrint, FaSyncAlt, FaTruck } from "react-icons/fa";
import "./CourierPanel.css";

const money = (value) => `Rs. ${Math.round(Number(value || 0)).toLocaleString()}`;

/**
 * Order page: book the parcel with a connected courier, then follow it.
 * After booking it shows the courier's status, the airway bill, and whether
 * the courier has paid the cash out yet.
 */
export default function CourierPanel({ orderId, onChanged }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => {
    axios.get(`/api/admin/orders/${orderId}/courier`).then((res) => {
      if (res.data.status !== 200) return;
      setData(res.data);
      const codes = Object.keys(res.data.options || {});
      if (codes.length && !res.data.shipment) {
        const first = codes[0];
        const match = res.data.options[first].match;
        setForm({
          courier: first,
          city_id: match ? match.id : "",
          city_name: match ? match.name : "",
          cod: res.data.defaults.cod,
          pieces: res.data.defaults.pieces,
          weight_grams: res.data.defaults.weight_grams,
          notes: "",
        });
      }
    }).catch(() => {});
  };

  useEffect(load, [orderId]);

  if (!data) return null;
  const { shipment, options, can_book: canBook } = data;
  const codes = Object.keys(options || {});

  const pickCourier = (code) => {
    const match = options[code].match;
    setForm((current) => ({ ...current, courier: code, city_id: match ? match.id : "", city_name: match ? match.name : "" }));
  };
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const book = (event) => {
    event.preventDefault();
    if (!form.city_id) {
      swal("Choose the city", "Pick the delivery city from the courier's list.", "warning");
      return;
    }
    setBusy("book");
    axios.post(`/api/admin/orders/${orderId}/courier-book`, form)
      .then((res) => {
        if (res.data.status === 200) {
          swal("Booked", res.data.message, "success");
          load();
          if (onChanged) onChanged();
        } else {
          swal("Not booked", res.data.message, "error");
        }
      })
      .catch((err) => swal("Not booked", err.response?.data?.message || "Please check the details.", "error"))
      .finally(() => setBusy(""));
  };

  const printLabel = () => {
    setBusy("label");
    axios.get(`/api/admin/shipments/${shipment.id}/label`, { responseType: "blob" })
      .then(async (res) => {
        const type = res.headers["content-type"] || "";
        if (type.includes("pdf")) {
          const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
          window.open(url, "_blank", "noopener");
          setTimeout(() => URL.revokeObjectURL(url), 60000);
          return;
        }
        const json = JSON.parse(await res.data.text());
        if (json.url) window.open(json.url, "_blank", "noopener");
        else swal("No airway bill", json.message || "The courier did not return an airway bill.", "error");
      })
      .catch(() => swal("No airway bill", "Could not get the airway bill. Please try again.", "error"))
      .finally(() => setBusy(""));
  };

  const refresh = () => {
    setBusy("refresh");
    axios.post(`/api/admin/orders/${orderId}/courier-refresh`)
      .then((res) => {
        if (res.data.summary?.errors?.length) swal("Tracking", res.data.message, "warning");
        load();
        if (onChanged) onChanged();
      })
      .finally(() => setBusy(""));
  };

  // Shipped already: show where the parcel is.
  if (shipment) {
    const tone = shipment.status === "delivered" ? "is-good" : shipment.status === "returned" || shipment.status === "cancelled" ? "is-bad" : "";
    return (
      <div className="admin-card mb-4 cp">
        <div className="admin-card-header d-flex justify-content-between align-items-center">
          <h5 className="admin-card-title mb-0"><FaTruck className="me-2" />Shipment</h5>
          {shipment.courier_code && <span className={`cp-status ${tone}`}>{shipment.status_label}</span>}
        </div>
        <div className="admin-card-body">
          <div className="cp-row"><span>Courier</span><strong>{shipment.courier}</strong></div>
          <div className="cp-row"><span>Tracking number</span><strong className="font-monospace">{shipment.tracking}</strong></div>
          {shipment.status_text && <div className="cp-row"><span>Courier says</span><strong>{shipment.status_text}</strong></div>}
          {shipment.courier_code && Number(shipment.cod) > 0 && (
            <div className="cp-row">
              <span>Cash to collect</span>
              <strong>
                {money(shipment.cod)}{" "}
                {shipment.paid_at ? <span className="cp-paid">paid out {shipment.paid_at.slice(0, 10)}</span> : <span className="cp-due">not paid out yet</span>}
              </strong>
            </div>
          )}
          {shipment.courier_code ? (
            <div className="cp-actions">
              {shipment.has_label && (
                <button type="button" className="cp-btn is-primary" onClick={printLabel} disabled={!!busy}>
                  <FaPrint size={11} /> {busy === "label" ? "Opening…" : "Print airway bill"}
                </button>
              )}
              <button type="button" className="cp-btn" onClick={refresh} disabled={!!busy}>
                <FaSyncAlt size={11} /> {busy === "refresh" ? "Updating…" : "Update tracking"}
              </button>
            </div>
          ) : (
            <p className="cp-note">Shipped by hand. Record the courier's payout in <Link to="/admin/couriers">Settings → Couriers</Link> when it pays you.</p>
          )}
          {shipment.synced_at && <p className="cp-note">Last checked {new Date(shipment.synced_at.replace(" ", "T")).toLocaleString()}. Updates every 30 minutes.</p>}
        </div>
      </div>
    );
  }

  if (!canBook) return null;

  if (!codes.length) {
    return (
      <div className="admin-card mb-4 cp">
        <div className="admin-card-body cp-empty">
          <FaTruck /> Book parcels with PostEx or Leopards right from here. <Link to="/admin/couriers">Connect a courier →</Link>
        </div>
      </div>
    );
  }

  const option = options[form?.courier];
  return (
    <form className="admin-card mb-4 cp" onSubmit={book}>
      <div className="admin-card-header">
        <h5 className="admin-card-title mb-0"><FaTruck className="me-2" />Book courier</h5>
      </div>
      <div className="admin-card-body">
        {codes.length > 1 && (
          <div className="cp-pick" role="group" aria-label="Courier">
            {codes.map((code) => (
              <button type="button" key={code} className={form.courier === code ? "is-active" : ""} onClick={() => pickCourier(code)}>{options[code].name}</button>
            ))}
          </div>
        )}
        {option?.error ? (
          <p className="text-danger small">{option.error}</p>
        ) : (
          <>
            <label className="form-label small fw-semibold">Delivery city {!option?.match && <span className="text-warning">· check this, no exact match</span>}</label>
            <select className="form-select form-select-sm mb-2" value={form.city_id} required
              onChange={(e) => {
                const city = option.cities.find((c) => String(c.id) === e.target.value);
                setForm((current) => ({ ...current, city_id: e.target.value, city_name: city ? city.name : "" }));
              }}>
              <option value="">Choose city</option>
              {option?.cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div className="row g-2">
              <div className="col-6">
                <label className="form-label small fw-semibold">Cash to collect (Rs.)</label>
                <input type="number" min="0" className="form-control form-control-sm" value={form.cod} onChange={(e) => set("cod", e.target.value)} required />
              </div>
              <div className="col-3">
                <label className="form-label small fw-semibold">Pieces</label>
                <input type="number" min="1" className="form-control form-control-sm" value={form.pieces} onChange={(e) => set("pieces", e.target.value)} required />
              </div>
              <div className="col-3">
                <label className="form-label small fw-semibold">Grams</label>
                <input type="number" min="1" className="form-control form-control-sm" value={form.weight_grams} onChange={(e) => set("weight_grams", e.target.value)} required />
              </div>
            </div>
            <label className="form-label small fw-semibold mt-2">Note for the rider <span className="text-muted fw-normal">(optional)</span></label>
            <input className="form-control form-control-sm" maxLength={250} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="e.g. Call before delivery" />
            <p className="cp-note">{data.defaults.details}</p>
            <button type="submit" className="cp-btn is-primary w-100 justify-content-center" disabled={!!busy}>
              {busy === "book" ? "Booking…" : `Book with ${option?.name || "courier"}`}
            </button>
            <p className="cp-note">This marks the order shipped and emails the customer the tracking number.</p>
          </>
        )}
      </div>
    </form>
  );
}
